import { useCoordination, renderCoordinationSummary } from '@/helpers/coordination'
import { defineStore } from 'pinia'
import { useCoreSocket } from '@/services/socket/core-socket'
import { useProjectGeneralCalculator, type SharedUsageStateItem } from '@/stores/projectCenter/projectGeneralCalculator'
import { useContentStore } from '@/stores/content'
import type { NestedObject } from '@/types'
import type { UsageItem, UsageCorrection } from '@/schemas/general/types'
import {
  type CompanySideOptions,
  DEFAULT_COUNTRY,
  type ProjectCalculationPart,
  type ProjectCalculationTierBoundary,
  type ProjectPhase as SocketProjectPhase,
  type SocketProjectCalculationResultData,
  type ProjectCalculationValues,
  type Risk,
  type ServiceRoleOptions,
  zod_ProjectPhase,
  type CoordinationBaseRole,
  type CoordinationBasis,
  zod_UsageCorrection,
} from '@arcexperts/core-types'
import type { ServiceSummaryListItems, SetupSummaryListItems } from '@/types'
import { renderSetupSummary as renderSetupSummaryPure } from '@/helpers/render-setup-summary'
import { renderServiceSummary as renderServiceSummaryPure } from '@/helpers/render-service-summary'
import {
  buildStoredServiceResultsV2,
  type LegacyStoredServiceHours,
  type PercentageRangeByPhase,
} from '@/helpers/service-results'
import { arrays_are_equal, deepClone, objects_are_equal } from '@/helpers/objects_arrays'
import type { PercentageRange, ProjectPhase as WidgetProjectPhase } from '@/types'
import type { Role } from '@/stores/projectCenter/useCalculatorForRole'

export interface SetupData {
  coordinationSurchargePercent?: number | null
  coordinationExcludedRoles?: CoordinationBaseRole[]
  coordinationJustification?: string
  buildingCost?: number
  constructionCost?: number
  usageCorrections: UsageCorrection[]
  risk?: Risk
  riskRemarks?: string
  serviceRole?: ServiceRoleOptions | null
}

type UsageItemWithBuildingContext = UsageItem & {
  facadeEnvelope?: number
  buildingTypeId?: string | null
  buildingTypeName?: string | null
  requirementsLevel?: 'LOW' | 'STANDARD' | 'HIGH' | 'VERY_HIGH' | string | null
  builtOverAreaRatio?: number | null
  unbuiltAreaRatio?: number | null
  aboveTerrainAreaRatio?: number | null
  belowTerrainAreaRatio?: number | null
  repetitionFactor?: number | null
  technologyFactor?: number | null
}

// SIA 103 total slab area factor: ratio total slab area / floor area maps linearly onto
// the factor (1:1 -> 1.0, 1.5:1 -> 1.5, 2:1 -> 2.0), clamped to that range.
const CIVIL_ENGINEER_SLAB_AREA_FACTOR_MIN = 1
const CIVIL_ENGINEER_SLAB_AREA_FACTOR_MAX = 2

function getCivilEngineerSlabAreaFactor(slabArea: number | null | undefined, quantityArea: number): number {
  if (!slabArea || slabArea <= 0 || quantityArea <= 0) return CIVIL_ENGINEER_SLAB_AREA_FACTOR_MIN

  const ratio = slabArea / quantityArea

  return Math.min(CIVIL_ENGINEER_SLAB_AREA_FACTOR_MAX, Math.max(CIVIL_ENGINEER_SLAB_AREA_FACTOR_MIN, ratio))
}

/**
 * Merges shared usages from General with role-specific corrections to create full UsageItem[].
 * This is needed because the backend expects UsageItem[] with all fields for calculations.
 */
function mergeUsagesWithCorrections(
  sharedUsages: SharedUsageStateItem[],
  corrections: UsageCorrection[]
): UsageItemWithBuildingContext[] {
  const correctionsByUsageIndex = new Map(corrections.map((correction) => [correction.usageIndex, correction]))

  return sharedUsages.map((shared, index) => {
    const correction = correctionsByUsageIndex.get(index)
    return {
      type: shared.type,
      typeCounter: shared.typeCounter,
      ratio: shared.ratio,
      buildingTypeId: shared.buildingTypeId ?? null,
      buildingTypeName: shared.buildingTypeName ?? null,
      requirementsLevel: shared.requirementsLevel ?? 'STANDARD',
      builtOverAreaRatio: shared.builtOverAreaRatio ?? 0,
      unbuiltAreaRatio: shared.unbuiltAreaRatio ?? 100,
      aboveTerrainAreaRatio: shared.aboveTerrainAreaRatio ?? 100,
      belowTerrainAreaRatio: shared.belowTerrainAreaRatio ?? 0,
      repetitionFactor: shared.repetitionFactor ?? 1.0,
      technologyFactor: shared.technologyFactor ?? 1.0,
      buildingPlan: shared.buildingPlan,
      rawConstruction: shared.rawConstruction,
      facadeEnvelope: shared.facadeEnvelope ?? shared.rawConstruction,
      interiors: shared.interiors,
      electricInstallation: shared.electricInstallation,
      heatingVentilation: shared.heatingVentilation,
      sanitary: shared.sanitary,
      // VAL-152: landscape usages carry their own reconstruction items
      landscapeFurnishing: shared.landscapeFurnishing,
      landscapePlanting: shared.landscapePlanting,
      landscapeSurfaces: shared.landscapeSurfaces,
      landscapeTopography: shared.landscapeTopography,
      landscapeSanitary: shared.landscapeSanitary,
      landscapeElectrical: shared.landscapeElectrical,
      manualArea: correction?.manualArea ?? undefined,
      manualComplexity: correction?.manualComplexity ?? undefined,
      complexityFactorOverride: correction?.complexityFactorOverride,
      // Legacy coordinate fields
      x1: correction?.x1 ?? null,
      y1: correction?.y1 ?? null,
      x2: correction?.x2 ?? null,
      y2: correction?.y2 ?? null,
      x3: correction?.x3 ?? null,
      y3: correction?.y3 ?? null,
      x4: correction?.x4 ?? null,
      y4: correction?.y4 ?? null,
      problem1: correction?.problem1 ?? undefined,
      problem2: correction?.problem2 ?? undefined,
      problem3: correction?.problem3 ?? undefined,
      problem4: correction?.problem4 ?? undefined,
      // New slider fields; default to 1.0 only for new projects with no legacy x/y coordinates.
      // For legacy projects, let the backend calculate from coordinates.
      sliderCreative: correction?.sliderCreative ?? (correction?.x1 != null ? undefined : 1.0),
      sliderTechnical: correction?.sliderTechnical ?? (correction?.x2 != null ? undefined : 1.0),
      sliderSpatial: correction?.sliderSpatial ?? (correction?.x3 != null ? undefined : 1.0),
      sliderOrganizational: correction?.sliderOrganizational ?? (correction?.x4 != null ? undefined : 1.0),
      complexityMode: correction?.complexityMode,
      sliderOverall: correction?.sliderOverall,
      // Justification fields
      justificationCreative: correction?.justificationCreative,
      justificationTechnical: correction?.justificationTechnical,
      justificationSpatial: correction?.justificationSpatial,
      justificationOrganizational: correction?.justificationOrganizational,
    }
  })
}

export interface ServiceData extends NestedObject {
  phases: WidgetProjectPhase[]
}

export type NestedNumberArrayObject = LegacyStoredServiceHours

export interface State {
  setupData: SetupData | null
  previousSetupData: SetupData | null
  defaultUsageItem: UsageItem | null
  general_buildingPlanServiceFactor: number
  minutesPerSquareMeterForUsages: number[]
  /** VAL-160: per usage, true when the building type has no own reference values for this role. */
  categoryReferenceForUsages: boolean[]
  /** VAL-102: per usage, the neighbouring interval's value when the area is close to an interval boundary. */
  tierBoundariesForUsages: (ProjectCalculationTierBoundary | null)[]
  complexityForUsages: number[]
  estimatedTotalHours: number
  coordinationBasis: CoordinationBasis | null
  estimatedBuildingCost: number

  serviceData: ServiceData | null
  previousServiceData: ServiceData | null
  defaultPhases: WidgetProjectPhase[]
  percentageRangeForPhases: PercentageRangeByPhase
  serviceHours: NestedNumberArrayObject | null
}

export type ServiceType =
  | 'architect'
  | 'landscape_architect'
  | 'civil_engineer'
  | 'civil_engineer_excavation'
  | 'engineer'
  | 'electrical'
  | 'heating_cooling'
  | 'ventilation_ac'
  | 'hvac'
  | 'sanitary'
  | 'building_automation'
  | 'specialist_coordination'
type SetupPart = Extract<ProjectCalculationPart, `${ServiceType}_setup`>
type ServicePart = Extract<ProjectCalculationPart, `${ServiceType}_service`>
const serviceRoleMap: Record<ServiceType, Role> = {
  architect: 'ARCHITECT',
  landscape_architect: 'LANDSCAPE_ARCHITECT',
  civil_engineer: 'CIVIL_ENGINEER',
  civil_engineer_excavation: 'CIVIL_ENGINEER_EXCAVATION',
  engineer: 'ENGINEER',
  electrical: 'ELECTRICAL',
  heating_cooling: 'HEATING_COOLING',
  ventilation_ac: 'VENTILATION_AC',
  hvac: 'HVAC',
  sanitary: 'SANITARY',
  building_automation: 'BUILDING_AUTOMATION',
  specialist_coordination: 'SPECIALIST_COORDINATION',
}
type ServiceCalculatorResult<Setup extends SetupPart, Service extends ServicePart> = Extract<
  SocketProjectCalculationResultData,
  { part: Setup | Service }
>

function normalizeSetupData(data: NestedObject | SetupData | undefined): SetupData | null {
  if (!data) return null

  const raw = data as Record<string, unknown>
  const usageCorrections = Array.isArray(raw.usageCorrections)
    ? raw.usageCorrections.map((item) => zod_UsageCorrection.parse(item))
    : []

  return {
    buildingCost: typeof raw.buildingCost === 'number' ? raw.buildingCost : undefined,
    constructionCost: typeof raw.constructionCost === 'number' ? raw.constructionCost : undefined,
    usageCorrections,
    coordinationSurchargePercent: raw.coordinationSurchargePercent as number | null | undefined,
    coordinationExcludedRoles: raw.coordinationExcludedRoles as CoordinationBaseRole[] | undefined,
    coordinationJustification: raw.coordinationJustification as string | undefined,
    risk: typeof raw.risk === 'string' ? (raw.risk as Risk) : undefined,
    riskRemarks: typeof raw.riskRemarks === 'string' ? raw.riskRemarks : undefined,
    serviceRole:
      typeof raw.serviceRole === 'string' || raw.serviceRole === null
        ? (raw.serviceRole as ServiceRoleOptions | null)
        : undefined,
  }
}

function normalizeServiceData(data: NestedObject | ServiceData | undefined): ServiceData | null {
  if (!data) return null

  const raw = data as Record<string, unknown>
  const phases = Array.isArray(raw.phases)
    ? raw.phases.map((item): WidgetProjectPhase => {
        const phase = zod_ProjectPhase.parse(item)
        return {
          active: phase.active,
          order: phase.order,
          percentage: phase.percentage,
          subPhases: phase.subPhases.map((subPhase) => ({
            order: subPhase.order,
            percentages: subPhase.percentages,
            additionalEfforts: subPhase.additionalEfforts ?? 0,
            remarks: subPhase.remarks ?? '',
          })),
        }
      })
    : []

  return { phases }
}

function buildSocketProjectPhases(phases: WidgetProjectPhase[]): SocketProjectPhase[] {
  return phases.map((phase) =>
    zod_ProjectPhase.parse({
      ...phase,
      subPhases: (phase.subPhases ?? []).map((subPhase) => ({
        ...subPhase,
        additionalEfforts: subPhase.additionalEfforts,
        remarks: subPhase.remarks,
      })),
    })
  )
}

function normalizeWidgetProjectPhases(phases: SocketProjectPhase[]): WidgetProjectPhase[] {
  return phases.map((phase) => ({
    active: phase.active,
    order: phase.order,
    percentage: phase.percentage,
    subPhases: phase.subPhases.map((subPhase) => ({
      order: subPhase.order,
      percentages: subPhase.percentages,
      additionalEfforts: subPhase.additionalEfforts ?? 0,
      remarks: subPhase.remarks ?? '',
    })),
  }))
}

function buildSetupCalculationPayload(
  setupData: SetupData,
  quantityArea: number,
  usageItems: UsageItemWithBuildingContext[],
  buildingPlanServiceFactorsForUsages?: number[],
  buildingPlanCostFactorsForUsages?: number[],
  civilEngineerSlabArea?: number | null,
  civilEngineerSlabAreaFactor?: number,
  includeArchitectSetupFields = false
): ProjectCalculationValues<SetupPart, 'complexityForUsages'> {
  const payload = {
    usageCorrections: setupData.usageCorrections,
    serviceRole: setupData.serviceRole,
    ...(includeArchitectSetupFields
      ? {
          buildingCost: setupData.buildingCost,
          constructionCost: setupData.constructionCost,
          risk: setupData.risk,
          riskRemarks: setupData.riskRemarks,
        }
      : {}),
  }

  return {
    ...payload,
    quantityArea,
    usageItems,
    ...(buildingPlanServiceFactorsForUsages ? { buildingPlanServiceFactorsForUsages } : {}),
    ...(buildingPlanCostFactorsForUsages ? { buildingPlanCostFactorsForUsages } : {}),
    ...(civilEngineerSlabArea !== undefined ? { civilEngineerSlabArea } : {}),
    ...(civilEngineerSlabAreaFactor !== undefined ? { civilEngineerSlabAreaFactor } : {}),
  }
}

function buildMinutesPerSquareMeterPayload(
  payload: ProjectCalculationValues<SetupPart, 'complexityForUsages'>,
  buildingPlanServiceFactor: number
): ProjectCalculationValues<SetupPart, 'minutesPerSquareMeterForUsages'> {
  return {
    ...payload,
    buildingPlanServiceFactor,
  }
}

function buildEstimatedTotalHoursPayload(
  payload: ProjectCalculationValues<SetupPart, 'complexityForUsages'>,
  buildingPlanServiceFactor: number
): ProjectCalculationValues<SetupPart, 'estimatedTotalHours'> {
  return {
    ...payload,
    buildingPlanServiceFactor,
  }
}

function buildEstimatedBuildingCostPayload(
  payload: ProjectCalculationValues<SetupPart, 'complexityForUsages'>,
  buildingPlanCostFactor: number,
  regionFactor: number
): ProjectCalculationValues<SetupPart, 'estimatedBuildingCost'> {
  return {
    ...payload,
    buildingPlanCostFactor,
    regionFactor,
  }
}

function buildDefaultPhasesPayload(
  serviceData: ServiceData,
  companySideOption?: CompanySideOptions
): ProjectCalculationValues<ServicePart, 'defaultPhases'> {
  return {
    phases: buildSocketProjectPhases(serviceData.phases),
    companySideOption,
  }
}

function buildPercentageRangePayload(
  serviceData: ServiceData,
  companySideOption?: CompanySideOptions,
  force?: boolean
): ProjectCalculationValues<ServicePart, 'percentageRangeForPhases'> {
  const payload = {
    phases: buildSocketProjectPhases(serviceData.phases),
    companySideOption,
  }
  if (force) {
    return {
      ...payload,
      forceReload: Date.now(),
    }
  }
  return payload
}

function buildServiceHoursPayload(
  serviceData: ServiceData,
  estimatedTotalHours: number,
  companySideOption?: CompanySideOptions
): ProjectCalculationValues<ServicePart, 'serviceHours'> {
  return {
    phases: buildSocketProjectPhases(serviceData.phases),
    estimatedTotalHours,
    companySideOption,
  }
}

export function createProjectServiceCalculator<Service extends ServiceType>(config: { serviceType: Service }) {
  const { serviceType } = config
  const storeName = `project${serviceType.charAt(0).toUpperCase() + serviceType.slice(1)}Calculator`
  const setupKey = `${serviceType}_setup` as Extract<ProjectCalculationPart, `${Service}_setup`>
  const serviceKey = `${serviceType}_service` as Extract<ProjectCalculationPart, `${Service}_service`>

  // SIA 105 usage split: the landscape architect derives from landscapeUsageItems/landscapeQuantityArea
  // in General, all other roles derive from the standard usageItems/quantityArea.
  const usesLandscapeUsages = serviceType === 'landscape_architect'
  const usesCivilEngineerAreaFactors = serviceType === 'civil_engineer'
  const usesSIA108TechnologyFactor = [
    'electrical',
    'heating_cooling',
    'ventilation_ac',
    'sanitary',
    'building_automation',
    'specialist_coordination',
  ].includes(serviceType)
  const usesArchitectSetupFields = serviceType === 'architect' || serviceType === 'landscape_architect'
  const role = serviceRoleMap[serviceType]
  type GeneralCalculatorStore = ReturnType<typeof useProjectGeneralCalculator>
  const getSharedUsagesForService = (generalCalc: GeneralCalculatorStore) => generalCalc.getSharedUsagesForRole(role)
  const getQuantityAreaForService = (generalCalc: GeneralCalculatorStore): number =>
    usesLandscapeUsages ? generalCalc.getLandscapeQuantityArea : generalCalc.getQuantityArea
  return defineStore(storeName, {
    state: (): State => ({
      setupData: null,
      previousSetupData: null,
      defaultUsageItem: null,
      general_buildingPlanServiceFactor: 1,
      minutesPerSquareMeterForUsages: [],
      categoryReferenceForUsages: [],
      tierBoundariesForUsages: [],
      complexityForUsages: [],
      estimatedTotalHours: 0,
      coordinationBasis: null,
      estimatedBuildingCost: 0,

      serviceData: null,
      previousServiceData: null,
      defaultPhases: [],
      percentageRangeForPhases: {},
      serviceHours: null,
    }),
    getters: {
      /**************************/
      /* Setup Part */
      /**************************/
      getDefaultUsageItem: (state) => state.defaultUsageItem,
      /**
       * Returns merged usage items combining shared usages from General with role-specific corrections.
       * Used for backend calculations that expect full UsageItem[].
       */
      getMergedUsageItems(): UsageItem[] {
        const generalCalc = useProjectGeneralCalculator()
        const sharedUsages = getSharedUsagesForService(generalCalc)
        const corrections = this.setupData?.usageCorrections ?? []
        return mergeUsagesWithCorrections(sharedUsages, corrections)
      },
      /**
       * Helper to get a correction for a specific usage index.
       */
      getCorrectionForIndex:
        (state) =>
        (index: number): UsageCorrection | undefined => {
          return state.setupData?.usageCorrections?.find((c) => c.usageIndex === index)
        },
      getMinutesPerSquareMeterForUsageAtIndexIgnoringManualArea: (state) => (index: number) => {
        return state.minutesPerSquareMeterForUsages[index] || 0
      },
      getMinutesPerSquareMeterForUsageAtIndexIgnoringManualComplexity() {
        return (index: number): number => {
          const correction = this.getCorrectionForIndex(index)
          if (correction?.manualArea) {
            return correction.manualArea
          }
          return this.minutesPerSquareMeterForUsages[index] || 0
        }
      },
      getComplexityForUsageAtIndex: (state) => (index: number) => {
        return state.complexityForUsages[index] || 1
      },
      getMinutesPerSquareMeterForUsageAtIndex() {
        return (index: number): number => {
          const correction = this.getCorrectionForIndex(index)
          if (correction?.manualComplexity && correction.complexityFactorOverride == null) {
            return correction.manualComplexity
          }

          const factor = correction?.complexityFactorOverride ?? this.getComplexityForUsageAtIndex(index)
          const minutesPerSquareMeter = correction?.manualArea ?? this.minutesPerSquareMeterForUsages[index]
          return minutesPerSquareMeter * factor || 0
        }
      },
      getEstimatedTotalHours: (state) => state.estimatedTotalHours,
      getEstimatedBuildingCost: (state) => state.estimatedBuildingCost,
      renderSetupSummary() {
        return (
          t: (key: string) => string,
          wt: (...path: string[]) => string,
          wtSetup: (...path: string[]) => string,
          wtSummary: (...path: string[]) => string
        ): SetupSummaryListItems => {
          const generalCalc = useProjectGeneralCalculator()
          const contentStore = useContentStore()
          if (serviceType === 'specialist_coordination' && this.coordinationBasis) {
            return renderCoordinationSummary(
              this.coordinationBasis,
              this.estimatedTotalHours,
              this.setupData?.coordinationJustification,
              wtSetup
            )
          }
          return renderSetupSummaryPure(
            {
              quantityArea: getQuantityAreaForService(generalCalc),
              sharedUsages: getSharedUsagesForService(generalCalc),
              isLandscapeArchitecture: usesLandscapeUsages,
              isCivilEngineer: usesCivilEngineerAreaFactors,
              isSIA108: usesSIA108TechnologyFactor,
              generalData: generalCalc.projectGeneral,
              corrections: this.setupData?.usageCorrections ?? [],
              minutesPerSquareMeterForUsages: this.minutesPerSquareMeterForUsages,
              complexityForUsages: this.complexityForUsages,
              complexityStepSize: contentStore.getComplexityStepSize(setupKey),
              estimatedBuildingCost: this.estimatedBuildingCost,
              risk: this.setupData?.risk,
              riskRemarks: this.setupData?.riskRemarks,
              buildingCost: this.setupData?.buildingCost,
              constructionCost: this.setupData?.constructionCost,
              resolveBuildingTypeName: contentStore.getBuildingTypeNameById,
            },
            t,
            wt,
            wtSetup,
            wtSummary
          )
        }
      },
      /**************************/
      /* Service Part */
      /**************************/
      getDefaultPhases: (state): WidgetProjectPhase[] => state.defaultPhases,
      getPercentageRangeForPhase:
        (state) =>
        (index: number): PercentageRange | null => {
          return state.percentageRangeForPhases[String(index + 1)] || null
        },
      getBasicServiceHours(): number {
        const totalHours = Math.round(this.estimatedTotalHours * 100) / 100
        return (totalHours / 100) * this.getTotalPercent
      },
      getHoursPerPhase:
        (state) =>
        (index: number): number => {
          if (state.serviceData?.phases && state.serviceData?.phases[index] && state.estimatedTotalHours) {
            const phase: WidgetProjectPhase = state.serviceData?.phases[index]
            if (phase.percentage && phase.active) {
              return (state.estimatedTotalHours / 100) * phase.percentage
            }
          }
          return 0
        },
      getTotalPercent: (state) => {
        if (state.serviceData?.phases) {
          const percentageArray = state.serviceData?.phases.filter(
            (item: WidgetProjectPhase) => item.percentage !== undefined && item.active
          )
          return percentageArray?.reduce((acc: number, curr: WidgetProjectPhase) => acc + (curr.percentage ?? 0), 0)
        }
        return 0
      },
      getTotalHoursPerPhase() {
        return (phaseIndex: number): number => {
          const phases = this.serviceData?.phases ?? this.defaultPhases
          const phase: WidgetProjectPhase | undefined = phases?.[phaseIndex]
          let sum = 0
          if (phase && phase.subPhases) {
            phase.subPhases.map((subphase, subPhaseIndex) => {
              sum += this.getServiceHoursSum(phaseIndex, subPhaseIndex)
              sum += subphase.additionalEfforts ?? 0
            })
          }
          return sum
        }
      },
      getServiceHours:
        (state) =>
        (phaseIndex: number, subPhaseIndex: number): number[] | null => {
          if (
            state.serviceHours &&
            state.serviceHours[phaseIndex + 1] &&
            state.serviceHours[phaseIndex + 1][subPhaseIndex + 1]
          ) {
            return state.serviceHours[phaseIndex + 1][subPhaseIndex + 1]
          }
          return null
        },
      getAdjustedServiceHours() {
        return (phaseIndex: number, subPhaseIndex: number): number[] | null => {
          const phases = this.serviceData?.phases ?? this.defaultPhases
          const subPhase = phases?.[phaseIndex]?.subPhases?.[subPhaseIndex]
          if (
            subPhase &&
            this.serviceHours &&
            this.serviceHours[phaseIndex + 1] &&
            this.serviceHours[phaseIndex + 1][subPhaseIndex + 1]
          ) {
            const serviceHours = this.serviceHours[phaseIndex + 1][subPhaseIndex + 1]
            const adjustedServiceHours: number[] = []
            serviceHours.map((hours, i) => {
              const percentage = subPhase?.percentages[i] ? subPhase?.percentages[i] / 100 : 0
              adjustedServiceHours.push(percentage * hours)
            })
            return adjustedServiceHours
          }
          return null
        }
      },
      getServiceHoursSum() {
        return (phaseIndex: number, subPhaseIndex: number): number => {
          let sum = 0
          if (
            this.serviceHours &&
            this.serviceHours[phaseIndex + 1] &&
            this.serviceHours[phaseIndex + 1][subPhaseIndex + 1]
          ) {
            const serviceHours = this.serviceHours[phaseIndex + 1][subPhaseIndex + 1]
            const adjustedServiceHours = this.getAdjustedServiceHours(phaseIndex, subPhaseIndex)
            serviceHours.map((hours, i) => {
              sum += hours + (adjustedServiceHours?.[i] ?? 0)
            })
          }
          return sum
        }
      },
      renderServiceSummary() {
        return (
          t: (key: string) => string,
          wt: (...path: string[]) => string,
          wtService: (...path: string[]) => string,
          wtSetup: (...path: string[]) => string,
          wtSummary: (...path: string[]) => string
        ): ServiceSummaryListItems => {
          const generalCalc = useProjectGeneralCalculator()
          const serviceResults = buildStoredServiceResultsV2({
            estimatedTotalHours: this.estimatedTotalHours,
            phases: this.serviceData?.phases ?? [],
            percentageRangeForPhases: this.percentageRangeForPhases,
            serviceHours: this.serviceHours,
          })

          return renderServiceSummaryPure(
            {
              country: generalCalc.projectGeneral?.country || DEFAULT_COUNTRY,
              focusTopics: generalCalc.projectGeneral?.focusTopics ?? [],
              serviceRole: this.setupData?.serviceRole,
              serviceResults,
            },
            t,
            wt,
            wtService,
            wtSetup,
            wtSummary
          )
        }
      },
    },
    actions: {
      initSetupData(data: NestedObject | undefined) {
        const normalized = normalizeSetupData(data)
        if (!normalized) return

        this.$patch({ setupData: normalized, previousSetupData: deepClone(normalized) })
        this.setComplexityForUsages() /* only on init because ... ? */
        this.setMinutesPerSquareMeterForUsages()
        this.setEstimatedTotalHours()
        this.setEstimatedBuildingCost()
      },
      setSetupData(data: NestedObject | undefined) {
        const normalized = normalizeSetupData(data)
        if (!normalized) return

        const setupChange = !objects_are_equal(normalized, this.previousSetupData)
        this.$patch({ setupData: normalized, previousSetupData: deepClone(normalized) })
        if (setupChange) {
          this.setMinutesPerSquareMeterForUsages()
          this.setEstimatedTotalHours()
          this.setEstimatedBuildingCost()
          // Previously Architect only set complexity on init, not on updates; this keeps recalculation consistent.
          this.setComplexityForUsages()
        }
      },
      /**
       * Called when shared usages or quantityArea change in General.
       * Triggers recalculation of all setup-related values.
       */
      onSharedUsagesChanged() {
        this.setMinutesPerSquareMeterForUsages()
        this.setEstimatedTotalHours()
        this.setEstimatedBuildingCost()
        this.setComplexityForUsages()
      },
      initServiceData(data: NestedObject | undefined) {
        const normalized = normalizeServiceData(data)
        if (!normalized) return

        this.$patch({ serviceData: normalized, previousServiceData: deepClone(normalized) })
        this.setInitialDefaultPhase()
        this.setPercentageRangeForPhases()
      },
      setServiceData(data: NestedObject | undefined) {
        const normalized = normalizeServiceData(data)
        if (!normalized) return

        const changedProjectPhase = !arrays_are_equal(normalized.phases, this.previousServiceData?.phases)
        this.$patch({ serviceData: normalized, previousServiceData: deepClone(normalized) })
        if (changedProjectPhase) {
          this.setServiceHours()
        }
      },
      /* setup */
      setDefaultUsageItem() {
        useCoreSocket().messageCenter?.getProjectDefaultObject(setupKey, 'defaultUsageItem')
      },
      /**
       * Builds payload for backend calculations by merging shared usages with corrections.
       * Backend expects usageItems[] and quantityArea in the payload.
       */
      buildSetupPayload(): ProjectCalculationValues<SetupPart, 'complexityForUsages'> | null {
        if (!this.setupData) {
          return null
        }

        const generalCalc = useProjectGeneralCalculator()
        const sharedUsages = getSharedUsagesForService(generalCalc)
        const quantityArea = getQuantityAreaForService(generalCalc)
        const corrections = this.setupData.usageCorrections
        const usageItems = mergeUsagesWithCorrections(sharedUsages, corrections)

        return buildSetupCalculationPayload(
          this.setupData,
          quantityArea,
          usageItems,
          undefined,
          undefined,
          usesCivilEngineerAreaFactors ? generalCalc.getCivilEngineerSlabArea : undefined,
          usesCivilEngineerAreaFactors
            ? getCivilEngineerSlabAreaFactor(generalCalc.getCivilEngineerSlabArea, quantityArea)
            : undefined,
          usesArchitectSetupFields
        )
      },
      setMinutesPerSquareMeterForUsages() {
        if (serviceType === 'specialist_coordination' && useCoordination().enabled.value) return
        const generalCalc = useProjectGeneralCalculator()
        const mergedUsages = mergeUsagesWithCorrections(
          getSharedUsagesForService(generalCalc),
          this.setupData?.usageCorrections ?? []
        )
        const setupPayload = this.buildSetupPayload()
        if (!mergedUsages.length) {
          return
        }
        if (!setupPayload) {
          return
        }
        useCoreSocket().messageCenter?.triggerProjectCalculation(
          setupKey,
          'minutesPerSquareMeterForUsages',
          buildMinutesPerSquareMeterPayload(setupPayload, generalCalc.getBuildingPlanServiceFactor)
        )
      },
      setComplexityForUsages() {
        if (serviceType === 'specialist_coordination' && useCoordination().enabled.value) return
        const generalCalc = useProjectGeneralCalculator()
        const mergedUsages = mergeUsagesWithCorrections(
          getSharedUsagesForService(generalCalc),
          this.setupData?.usageCorrections ?? []
        )
        const setupPayload = this.buildSetupPayload()
        if (!mergedUsages.length) {
          return
        }
        if (!setupPayload) {
          return
        }
        useCoreSocket().messageCenter?.triggerProjectCalculation(setupKey, 'complexityForUsages', setupPayload)
      },
      setEstimatedTotalHours() {
        if (serviceType === 'specialist_coordination' && useCoordination().enabled.value) {
          if (!this.setupData) return
          const { disciplines } = useCoordination()
          useCoreSocket().messageCenter?.triggerProjectCalculation(
            'specialist_coordination_setup',
            'estimatedTotalHours',
            {
              usageCorrections: [],
              quantityArea: 0,
              usageItems: [],
              coordinationSurchargePercent: this.setupData.coordinationSurchargePercent,
              coordinationExcludedRoles: this.setupData.coordinationExcludedRoles ?? [],
              coordinationJustification: this.setupData.coordinationJustification,
              coordinationHours: disciplines.value.map(({ role, hours }) => ({ role, hours })),
            }
          )
          return
        }
        const generalCalc = useProjectGeneralCalculator()
        const mergedUsages = mergeUsagesWithCorrections(
          getSharedUsagesForService(generalCalc),
          this.setupData?.usageCorrections ?? []
        )
        const setupPayload = this.buildSetupPayload()
        if (!mergedUsages.length) {
          return
        }
        if (!setupPayload) {
          return
        }
        useCoreSocket().messageCenter?.triggerProjectCalculation(
          setupKey,
          'estimatedTotalHours',
          buildEstimatedTotalHoursPayload(setupPayload, generalCalc.getBuildingPlanServiceFactor)
        )
      },
      setEstimatedBuildingCost() {
        if (serviceType === 'specialist_coordination' && useCoordination().enabled.value) return
        const generalCalc = useProjectGeneralCalculator()
        const mergedUsages = mergeUsagesWithCorrections(
          getSharedUsagesForService(generalCalc),
          this.setupData?.usageCorrections ?? []
        )
        const setupPayload = this.buildSetupPayload()
        if (!mergedUsages.length) {
          return
        }
        if (!setupPayload) {
          return
        }
        useCoreSocket().messageCenter?.triggerProjectCalculation(
          setupKey,
          'estimatedBuildingCost',
          buildEstimatedBuildingCostPayload(
            setupPayload,
            generalCalc.getBuildingPlanCostFactor,
            generalCalc.getRegionFactor
          )
        )
      },
      /* service */
      setInitialDefaultPhase(force?: boolean) {
        if (this.defaultPhases.length && !force) {
          return
        }
        if (!this.serviceData) {
          return
        }
        useCoreSocket().messageCenter?.triggerProjectCalculation(
          serviceKey,
          'defaultPhases',
          buildDefaultPhasesPayload(this.serviceData, useProjectGeneralCalculator()?.projectGeneral?.companySideOption)
        )
      },
      setPercentageRangeForPhases(force?: boolean) {
        // Unified to always reload like architect when forced; this may still deserve a later review.
        if (!this.serviceData) {
          return
        }
        useCoreSocket().messageCenter?.triggerProjectCalculation(
          serviceKey,
          'percentageRangeForPhases',
          buildPercentageRangePayload(
            this.serviceData,
            useProjectGeneralCalculator()?.projectGeneral?.companySideOption,
            force
          )
        )
      },
      setServiceHours() {
        if (!this.serviceData?.phases?.length) {
          return
        }
        useCoreSocket().messageCenter?.triggerProjectCalculation(
          serviceKey,
          'serviceHours',
          buildServiceHoursPayload(
            this.serviceData,
            this.estimatedTotalHours,
            useProjectGeneralCalculator()?.projectGeneral?.companySideOption
          )
        )
      },
      setResult(result: ServiceCalculatorResult<SetupPart, ServicePart>) {
        switch (result.request) {
          case 'defaultUsageItem':
            this.defaultUsageItem = result.value
            break
          case 'minutesPerSquareMeterForUsages':
            this.minutesPerSquareMeterForUsages = result.value
            this.categoryReferenceForUsages = result.categoryReferenceForUsages ?? []
            this.tierBoundariesForUsages = result.tierBoundariesForUsages ?? []
            break
          case 'complexityForUsages':
            this.complexityForUsages = result.value
            break
          case 'estimatedTotalHours':
            this.estimatedTotalHours = result.value
            this.coordinationBasis = result.coordinationBasis ?? null
            break
          case 'estimatedBuildingCost':
            this.estimatedBuildingCost = result.value
            break
          case 'defaultPhases':
            this.defaultPhases = normalizeWidgetProjectPhases(result.value)
            break
          case 'percentageRangeForPhases':
            this.percentageRangeForPhases = result.value
            break
          case 'serviceHours':
            this.serviceHours = result.value
        }

        // estimatedTotalHours is async; recompute serviceHours after the new total arrives.
        if (result.request === 'estimatedTotalHours') {
          this.setServiceHours()
        }

        // Handle migrated sliders from backend (silent migration from old coordinates)
        if (result.migratedSliders && result.migratedSliders.length > 0 && this.setupData?.usageCorrections) {
          const corrections = deepClone(this.setupData.usageCorrections)
          let needsSave = false

          result.migratedSliders.forEach((migration) => {
            const existingIndex = corrections.findIndex((c: UsageCorrection) => c.usageIndex === migration.usageIndex)
            if (existingIndex >= 0) {
              const correction = corrections[existingIndex]
              // Only migrate if slider values do not exist yet
              if (correction.sliderCreative === undefined) {
                correction.sliderCreative = migration.sliderCreative
                correction.sliderTechnical = migration.sliderTechnical
                correction.sliderSpatial = migration.sliderSpatial
                correction.sliderOrganizational = migration.sliderOrganizational

                // Migrate old problem texts to justification fields
                if (correction.problem1) {
                  correction.justificationCreative = correction.problem1
                }
                if (correction.problem2) {
                  correction.justificationTechnical = correction.problem2
                }
                if (correction.problem3) {
                  correction.justificationSpatial = correction.problem3
                }
                if (correction.problem4) {
                  correction.justificationOrganizational = correction.problem4
                }

                // Remove old coordinate and problem fields after migration
                correction.x1 = undefined
                correction.y1 = undefined
                correction.x2 = undefined
                correction.y2 = undefined
                correction.x3 = undefined
                correction.y3 = undefined
                correction.x4 = undefined
                correction.y4 = undefined
                correction.problem1 = undefined
                correction.problem2 = undefined
                correction.problem3 = undefined
                correction.problem4 = undefined

                needsSave = true
              }
            }
          })

          if (needsSave) {
            this.$patch({
              setupData: {
                ...this.setupData,
                usageCorrections: corrections,
              },
            })
          }
        }
      },
    },
  })
}

export const useProjectArchitectCalculator = createProjectServiceCalculator({ serviceType: 'architect' })
export const useProjectLandscapeArchitectCalculator = createProjectServiceCalculator({
  serviceType: 'landscape_architect',
})
export const useProjectCivilEngineerCalculator = createProjectServiceCalculator({ serviceType: 'civil_engineer' })
export const useProjectCivilEngineerExcavationCalculator = createProjectServiceCalculator({
  serviceType: 'civil_engineer_excavation',
})
export const useProjectEngineerCalculator = createProjectServiceCalculator({ serviceType: 'engineer' })
export const useProjectElectricalCalculator = createProjectServiceCalculator({ serviceType: 'electrical' })
export const useProjectHeatingCoolingCalculator = createProjectServiceCalculator({ serviceType: 'heating_cooling' })
export const useProjectVentilationACCalculator = createProjectServiceCalculator({ serviceType: 'ventilation_ac' })
export const useProjectHVACCalculator = createProjectServiceCalculator({ serviceType: 'hvac' })
export const useProjectSanitaryCalculator = createProjectServiceCalculator({ serviceType: 'sanitary' })
export const useProjectBuildingAutomationCalculator = createProjectServiceCalculator({
  serviceType: 'building_automation',
})
export const useProjectSpecialistCoordinationCalculator = createProjectServiceCalculator({
  serviceType: 'specialist_coordination',
})
